import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { parseFractionLikeInput as parseFraction } from "../../../../components/inputs/FractionInput";
import InscribedTriangleDiagram from "../../../../components/diagrams/InscribedTriangleDiagram";

const DEC_TOL = 0.1;
const nice = [20, 24, 30, 36, 40, 45, 50, 54, 60, 72, 75, 80];

/* ===== helpers ===== */
function preprocessRaw(raw) {
  if (raw == null) return "";
  return String(raw).trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}
function pretty(x) {
  const s = String(x ?? "").trim();
  if (!s) return "";
  return s.replace(/\s+/g, "").replace(".", "{,}");
}
// wyciągamy tekst z różnych możliwych kształtów wartości (string / number / {raw} / {value} / {input})
function extractRaw(maybe) {
  if (maybe == null) return "";
  if (typeof maybe === "string" || typeof maybe === "number") return String(maybe);
  if (typeof maybe === "object") {
    if ("raw" in maybe) return String(maybe.raw ?? "");
    if ("value" in maybe) return String(maybe.value ?? "");
    if ("input" in maybe) return String(maybe.input ?? "");
  }
  return "";
}
// normalizujemy to, co przychodzi z onChange, do czystego stringa
function normalizeChange(v) {
  return typeof v === "string" ? v : extractRaw(v);
}

/* ===== generator ===== */
export function generateTask() {
  let aA = nice[Math.floor(Math.random() * nice.length)];
  let aB = nice[Math.floor(Math.random() * nice.length)];
  while (aA + aB >= 170) aB = nice[Math.floor(Math.random() * nice.length)];
  const aC = 180 - (aA + aB);

  // losowo zamieniamy, który P idzie na który łuk
  const swap = Math.random() < 0.5;
  return {
    type: "FindThirdAngleInInscribedTriangleTask",
    data: {
      aA, aB, aC,
      p1Arc: swap ? "CA" : "BC", // BC ↔ α_A, CA ↔ α_B
      p2Arc: swap ? "BC" : "CA",
    },
  };
}

/* ===== treść ===== */
export function renderTask({ data }) {
  const { aA, aB, aC, p1Arc, p2Arc } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      W okręgu dany jest trójkąt wpisany <InlineMath math={"ABC"} />.
      Znamy wartości dwóch <b>innych</b> kątów wpisanych w punktach{" "}
      <InlineMath math={"P_1"} /> i <InlineMath math={"P_2"} />:
      <InlineMath math={`\\;P_1 = ${aA}^{\\circ}\\;`} /> oraz{" "}
      <InlineMath math={`P_2 = ${aB}^{\\circ}`} />. Oblicz kąt{" "}
      <span style={{ whiteSpace: "nowrap" }}><InlineMath math={"\\alpha_{C}"} />{"."}</span>
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <InscribedTriangleDiagram
          /* na rysunku: tylko α_C z podpisem, P1/P2 bez liczb */
          labelA="" labelB="" labelC={"\\alpha_{C}"}
          anglesDegA={aA} anglesDegB={aB} anglesDegC={aC}
          showP1 p1Arc={p1Arc} p1ShiftDeg={95}
          showP2 p2Arc={p2Arc} p2ShiftDeg={-95}
          // twardy dystans między punktami: co najmniej promień po łuku
          minArcGapPx={158} gapMode="arc"
          size={285} circleR={108}
        />
      </div>
    </div>
  );
}

/* ===== input ===== */
export function renderInput(_task, userAnswer, onChange, disabled) {
  const raw = extractRaw(userAnswer);
  return (
    <FractionInput
      value={raw} // FractionInput wymaga STRINGA
      onChange={(v) => onChange({ raw: normalizeChange(v) })}
      disabled={disabled}
          labelLatex={"\\alpha_{C}"}
      suffixLatex="°"
      placeholder="np. 48"
    />
  );
}

/* ===== check ===== */
export function checkAnswer({ data }, userAnswer) {
  const raw = extractRaw(userAnswer).trim();
  if (raw === "") return false;
  const val = parseFraction(preprocessRaw(raw));
  if (!Number.isFinite(val)) return false;
  return Math.abs(val - data.aC) <= DEC_TOL;
}

/* ===== answer ===== */
export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\alpha_{C} = ${data.aC}^{\\circ}`} />
    </div>
  );
}

/* ===== explanation ===== */
export function renderExplanation({ data }) {
  const { aA, aB, aC, p1Arc, p2Arc } = data;

  const whichEquals = (arc) => (arc === "BC" ? "A" : arc === "CA" ? "B" : "C");
  const P1eq = whichEquals(p1Arc);
  const P2eq = whichEquals(p2Arc);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Kąty wpisane oparte na tym samym łuku są równe, więc{" "}
        {P1eq === "A" && <BlockMath math={`P_1=\\alpha_A=${aA}^{\\circ}`} />}
        {P1eq === "B" && <BlockMath math={`P_1=\\alpha_B=${aA}^{\\circ}`} />}
        {P2eq === "A" && <BlockMath math={`\\;\\;P_2=\\alpha_A=${aB}^{\\circ}`} />}
        {P2eq === "B" && <BlockMath math={`\\;\\;P_2=\\alpha_B=${aB}^{\\circ}`} />}
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong>{" "}
        <BlockMath
          math={String.raw`\alpha_{C}=180^{\circ}-\alpha_{A}-\alpha_{B}
          =180^{\circ}-${aA}^{\circ}-${aB}^{\circ}
          =${aC}^{\circ}`}
        />
      </div>
    </div>
  );
}

/* ===== user answer preview ===== */
export function renderUserAnswer(userAnswer) {
  const raw = extractRaw(userAnswer).trim();
  if (raw === "") return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\displaystyle \\alpha_{C} = ${pretty(raw)}^{\\circ}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
